import type {
  DiagnoseRequest,
  DiagnoseTransport,
  K8sdiagCapability,
  K8sdiagEvent,
  K8sdiagFocus,
  K8sdiagModel,
  K8sdiagModels,
  K8sdiagSession,
  K8sdiagSessionTurns,
  K8sdiagTurn,
} from "./types";

/** 대시보드 BFF의 진단 프록시. 호스트가 다른 경로를 쓰면 transport를 넘깁니다. */
export const defaultTransport: DiagnoseTransport = (path, init) =>
  fetch(`/api/v1/k8sdiag${path}`, { credentials: "same-origin", ...init });

const EVENTS = new Set(["context_used", "tool_call", "tool_result", "message", "error"]);
/** 한 스트림이 실어 나를 수 있는 상한입니다. 상류가 끝내지 않아도 여기서 끝납니다. */
const MAX_STREAM_BYTES = 1 << 20;

/**
 * 패널을 띄울지 묻습니다. 401·미설정(503)·연결 실패는 전부 `null`입니다 —
 * 셋 다 "패널을 렌더하지 않는다"는 같은 결론이고, 화면에 오류를 띄우면
 * 사용자가 권한을 요청하면 되는 기능으로 읽습니다.
 */
export async function fetchDiagnoseCapability(
  transport: DiagnoseTransport,
  signal: AbortSignal,
  namespace?: string,
): Promise<K8sdiagCapability | null> {
  const query = namespace ? `?namespace=${encodeURIComponent(namespace)}` : "";
  try {
    const res = await transport(`/capability${query}`, { headers: { accept: "application/json" }, signal });
    if (!res.ok || !res.headers.get("content-type")?.startsWith("application/json")) {
      await res.body?.cancel();
      return null;
    }
    const value = (await res.json()) as K8sdiagCapability;
    return typeof value?.available === "boolean" ? value : null;
  } catch {
    return null;
  }
}

/**
 * viewer 등록·해제를 보냅니다. 응답은 쓰지 않지만 본문은 닫습니다 — 그대로 두면
 * 브라우저가 요청을 문서가 바뀔 때까지 붙잡습니다. (#183)
 */
async function sendViewer(transport: DiagnoseTransport, init: RequestInit): Promise<void> {
  try {
    await (await transport("/viewer", init)).body?.cancel();
  } catch {
    /* 힌트가 없을 뿐입니다. 등록은 TTL로도 사라집니다. */
  }
}

/**
 * 지금 보고 있는 화면을 등록합니다. `host`는 여기서 정하지 않습니다 — 서버가
 * 고정합니다. 실패는 조용히 무시합니다: 좌표가 없으면 서비스가 되물을 뿐이고,
 * 진단이 아닌 화면 이동이 실패 알림을 띄울 이유가 없습니다. (ADR-0005 §1)
 */
export function putViewer(transport: DiagnoseTransport, focus: K8sdiagFocus): Promise<void> {
  return sendViewer(transport, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ focus }),
  });
}

export function deleteViewer(transport: DiagnoseTransport): Promise<void> {
  return sendViewer(transport, { method: "DELETE" });
}

export async function fetchModels(
  transport: DiagnoseTransport,
  signal: AbortSignal,
): Promise<K8sdiagModels | null> {
  try {
    const res = await transport("/models", { headers: { accept: "application/json" }, signal });
    if (!res.ok) {
      await res.body?.cancel();
      return null;
    }
    const value = (await res.json()) as { models?: (string | K8sdiagModel)[]; default?: string };
    if (!Array.isArray(value?.models)) return null;
    return {
      models: value.models.map((m) => (typeof m === "string" ? { id: m, label: m } : m)),
      default: value.default ?? "",
    };
  } catch {
    return null;
  }
}

export async function fetchSessions(
  transport: DiagnoseTransport,
  signal: AbortSignal,
): Promise<K8sdiagSession[]> {
  try {
    const res = await transport("/sessions", { headers: { accept: "application/json" }, signal });
    if (!res.ok) {
      await res.body?.cancel();
      return [];
    }
    const value = (await res.json()) as { sessions?: K8sdiagSession[] };
    return Array.isArray(value?.sessions) ? value.sessions : [];
  } catch {
    return [];
  }
}

export async function fetchSessionTurns(transport: DiagnoseTransport, id: string): Promise<K8sdiagTurn[]> {
  try {
    const res = await transport(`/sessions/${encodeURIComponent(id)}`, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) {
      await res.body?.cancel();
      return [];
    }
    const value = (await res.json()) as K8sdiagSessionTurns;
    return Array.isArray(value?.turns) ? value.turns : [];
  } catch {
    return [];
  }
}

export async function deleteSession(transport: DiagnoseTransport, id: string): Promise<void> {
  try {
    await transport(`/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
  } catch {
    /* 목록을 다시 읽으면 사실이 드러납니다. */
  }
}

/**
 * 질문 하나를 SSE로 실행합니다. 스트림이 시작된 뒤의 실패는 HTTP 상태가 아니라
 * `event: error`로 오므로 상태 코드만 보면 실패를 놓칩니다.
 * 반환값은 이어지는 질문에 되돌려 줄 세션 식별자입니다.
 */
export async function streamDiagnose(
  transport: DiagnoseTransport,
  body: DiagnoseRequest,
  onEvent: (event: K8sdiagEvent) => void,
  signal: AbortSignal,
): Promise<string> {
  const res = await transport("/diagnose/stream", {
    method: "POST",
    headers: { "content-type": "application/json", accept: "text/event-stream" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    await res.body?.cancel();
    throw new Error(`diagnose_rejected_${res.status}`);
  }
  const sessionId = res.headers.get("X-Session-Id") ?? "";
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let seen = 0;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (value) {
        seen += value.byteLength;
        if (seen > MAX_STREAM_BYTES) throw new Error("diagnose_stream_too_large");
        buffer += decoder.decode(value, { stream: true });
      }
      let end = buffer.indexOf("\n\n");
      while (end >= 0) {
        emit(buffer.slice(0, end), onEvent);
        buffer = buffer.slice(end + 2);
        end = buffer.indexOf("\n\n");
      }
      if (done) {
        emit(buffer, onEvent);
        break;
      }
    }
  } finally {
    reader.releaseLock();
  }
  return sessionId;
}

/** 계약을 벗어난 프레임은 조용히 버립니다 — 알 수 없는 이벤트는 사실이 아닙니다. */
function emit(frame: string, onEvent: (event: K8sdiagEvent) => void) {
  let event = "message";
  const data: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data.push(line.slice(5).replace(/^ /, ""));
  }
  if (!data.length || !EVENTS.has(event)) return;
  try {
    onEvent({ event, data: JSON.parse(data.join("\n")) } as K8sdiagEvent);
  } catch {
    /* JSON이 아닌 data는 이 계약에 없습니다. */
  }
}
